import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pause, Play } from 'lucide-react';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { CaptureCarousel } from './CaptureCarousel';
import type { CaptureMetier } from './vitrineMetiers';
import type { ChapitreVideo } from './vitrineParcours';

/**
 * Parcours en vidéo d'un module : de vrais extraits d'écran de l'application, lus l'un après l'autre dans l'ordre où l'on
 * s'en sert. La liste des étapes reste visible et cliquable ; la lecture ne tourne que lorsque le bloc est à l'écran, avec un
 * bouton pause. Si l'appareil demande moins d'animation, ne sait pas lire la vidéo ou si un fichier ne charge pas, on retombe
 * sur les captures fixes du module.
 */
const lisibleWebm = (): boolean => {
    if (typeof document === 'undefined') return true;
    const v = document.createElement('video');
    return !!v.canPlayType && v.canPlayType('video/webm; codecs="vp9"') !== '';
};

export const ParcoursVideo: React.FC<{ chapitres: ChapitreVideo[]; repli: CaptureMetier[]; module: string }> = ({ chapitres, repli, module }) => {
    const { tokens } = useAmbiance();
    const n = chapitres.length;
    const [i, setI] = useState(0);
    const [lecture, setLecture] = useState(true);
    const [enVue, setEnVue] = useState(false);
    const [progres, setProgres] = useState(0);
    const [erreur, setErreur] = useState(false);
    const section = useRef<HTMLElement>(null);
    const video = useRef<HTMLVideoElement>(null);
    const sansAnimation = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const aller = useCallback((k: number) => { setProgres(0); setI(((k % n) + n) % n); }, [n]);
    const enMarche = lecture && enVue;

    useEffect(() => {
        const el = section.current;
        if (!el || typeof IntersectionObserver === 'undefined') { setEnVue(true); return undefined; }
        const obs = new IntersectionObserver(([e]) => setEnVue(e.isIntersecting), { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // Lecture / pause selon la visibilité et le bouton ; à chaque nouvelle étape, le nouvel extrait démarre
    useEffect(() => {
        const v = video.current;
        if (!v) return;
        if (enMarche) { const p = v.play(); if (p && typeof p.catch === 'function') p.catch(() => undefined); } else v.pause();
    }, [enMarche, i]);

    // Prépare l'extrait suivant pour que l'enchaînement soit sans attente
    useEffect(() => {
        if (n < 2 || !enVue) return undefined;
        const pre = document.createElement('video');
        pre.preload = 'auto'; pre.muted = true; pre.src = chapitres[(i + 1) % n].src;
        return () => { pre.removeAttribute('src'); pre.load(); };
    }, [i, n, enVue, chapitres]);

    if (sansAnimation || erreur || !lisibleWebm()) return <CaptureCarousel captures={repli} />;

    const c = chapitres[i];
    const colonnes = c.points.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : c.points.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

    return (
        <section ref={section} className="max-w-[1160px] mx-auto px-5 pt-8 pb-14 md:pt-10 md:pb-20" aria-label={`Parcours en vidéo : ${module}`}>
            <div className="rounded-2xl border border-border bg-surface shadow-[0_24px_60px_-20px_rgba(0,0,0,.28)] overflow-hidden">
                <div className="flex items-center gap-1.5 h-9 px-4 border-b border-border bg-surface-soft" aria-hidden="true">
                    <span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" />
                    <span className="ml-3 text-[11px] font-mono text-content-muted truncate">l-iamani.com · {module}</span>
                </div>
                <div className="flex gap-1.5 px-4 pt-3 bg-surface-soft" aria-hidden="true">
                    {chapitres.map((x, k) => (
                        <span key={x.src} className="relative h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                            {k <= i && <span className="absolute inset-y-0 left-0 rounded-full" style={{ background: tokens.acc, width: k < i ? '100%' : `${Math.round(progres * 100)}%` }} />}
                        </span>
                    ))}
                </div>
                <div className="bg-surface-soft p-3 md:p-5">
                    <video
                        key={c.src}
                        ref={video}
                        src={c.src}
                        muted
                        playsInline
                        preload={enVue ? "auto" : "none"}
                        className="block w-full aspect-video rounded-lg border border-border shadow-md bg-black/5"
                        aria-label={c.titre}
                        onEnded={() => aller(i + 1)}
                        onError={() => setErreur(true)}
                        onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgres(v.currentTime / v.duration); }}
                    />
                </div>
            </div>

            <div className="mt-7" aria-live="polite">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <h3 key={c.titre} className="text-[22px] md:text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] max-w-[760px]">{c.titre}</h3>
                    <button type="button" onClick={() => setLecture((v) => !v)} aria-label={lecture ? 'Mettre la lecture en pause' : 'Lancer la lecture'} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-[12px] font-semibold hover:border-content transition-colors shrink-0">
                        {lecture ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}{lecture ? 'Pause' : 'Lecture'}
                    </button>
                </div>

                <ol className="flex flex-col md:flex-row gap-2 mb-5" aria-label="Étapes du parcours">
                    {chapitres.map((x, k) => (
                        <li key={x.src} className="md:flex-1 min-w-0">
                            <button
                                type="button"
                                onClick={() => aller(k)}
                                aria-current={k === i ? 'step' : undefined}
                                className={`w-full h-full flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${k === i ? 'bg-surface border-content' : 'bg-surface-soft border-border hover:border-content'}`}
                            >
                                <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold text-white tabular-nums" style={{ background: k === i ? tokens.acc : 'var(--content-muted, #8a8a8a)' }} aria-hidden="true">{k + 1}</span>
                                <span className={`text-[13px] leading-snug ${k === i ? 'font-semibold text-content' : 'text-content-muted'}`}>{x.titre}</span>
                            </button>
                        </li>
                    ))}
                </ol>

                <ul className={`grid grid-cols-1 gap-3 ${colonnes}`}>
                    {c.points.map((t) => (
                        <li key={t} className="flex gap-3 rounded-xl border border-border bg-surface p-4 text-[14px] text-content-muted leading-snug">
                            <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ background: tokens.acc }} aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                            <span>{t}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};
